import { useEffect, useState } from "react";
import { Satellite, Layers, Loader2, ImageOff, Lock } from "lucide-react";

const ImgFrame = ({ url, alt, minH = "h-56" }) => {
  const [state, setState] = useState("loading");
  useEffect(() => {
    setState("loading");
  }, [url]);
  return (
    <div className={`relative w-full ${minH} bg-atlas-void/70 border border-white/10 overflow-hidden`}>
      {state !== "error" && (
        <img
          src={url}
          alt={alt}
          loading="lazy"
          onLoad={() => setState("ok")}
          onError={() => setState("error")}
          className={`w-full h-full object-cover transition-opacity duration-300 ${state === "ok" ? "opacity-100" : "opacity-0"}`}
        />
      )}
      {state === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-4 h-4 text-atlas-cyan animate-spin" />
        </div>
      )}
      {state === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-atlas-secondary/75">
          <ImageOff className="w-4 h-4" />
          <span className="text-[9px] font-mono uppercase tracking-widest">No pass for this date</span>
        </div>
      )}
    </div>
  );
};

// Phase-1 "why now" current-state imagery, with a layer switcher.
export const SatelliteImagery = ({ imagery, testId, compact = false }) => {
  const [active, setActive] = useState(0);
  if (!imagery || !Array.isArray(imagery.layers) || imagery.layers.length === 0) return null;
  const layer = imagery.layers[Math.min(active, imagery.layers.length - 1)];

  return (
    <div className={compact ? "mt-2" : "mb-4"} data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Satellite className="w-3.5 h-3.5 text-atlas-cyan" />
        <span className="text-[10px] uppercase tracking-widest text-atlas-cyan/60 font-mono">
          Satellite View{compact ? "" : " · Precursor Evidence"}
        </span>
        <span className="ml-auto text-[9px] font-mono text-atlas-secondary/75">{layer.date}</span>
      </div>

      <ImgFrame url={layer.url} alt={layer.label} minH={compact ? "h-40" : "h-56"} />

      {imagery.layers.length > 1 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {imagery.layers.map((l, i) => (
            <button
              key={l.id}
              onClick={() => setActive(i)}
              data-testid={`${testId}-layer-${i}`}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] font-mono transition-colors ${
                i === active
                  ? "bg-atlas-cyan text-atlas-void border-atlas-cyan"
                  : "text-atlas-secondary border-white/15 hover:text-white hover:border-white/30"
              }`}
            >
              <Layers className="w-2.5 h-2.5" />
              {l.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1 text-[8px] font-mono text-atlas-secondary/75 mt-1 leading-relaxed">
        <Lock className="w-2.5 h-2.5 shrink-0" />
        {imagery.note || `Imagery ≤ data cutoff · ${imagery.attribution}`}
      </div>
      <div className="text-[8px] font-mono text-atlas-secondary/75">{imagery.attribution}</div>
    </div>
  );
};

// Phase-2 "we called it" before/after verification pair (resolved bulletins only).
export const BeforeAfterImagery = ({ pair, testId }) => {
  if (!pair || !pair.before || !pair.after) return null;
  return (
    <div className="mt-2" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Satellite className="w-3.5 h-3.5 text-atlas-red" />
        <span className="text-[10px] uppercase tracking-widest text-atlas-red/85 font-mono">
          Before / After · Outcome Verification
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <ImgFrame url={pair.before.url} alt="before" minH="h-32" />
          <div className="text-[8px] font-mono text-atlas-secondary/80 mt-0.5">{pair.before.label} · {pair.before.date}</div>
        </div>
        <div>
          <ImgFrame url={pair.after.url} alt="after" minH="h-32" />
          <div className="text-[8px] font-mono text-atlas-red/85 mt-0.5">{pair.after.label} · {pair.after.date}</div>
        </div>
      </div>
      <div className="text-[8px] font-mono text-atlas-secondary/75 mt-1 leading-relaxed">{pair.note} · {pair.attribution}</div>
    </div>
  );
};
